/**
 * 主题模式 Provider（T01 / 架构 §1.1）
 *  - 包裹 MUI ThemeProvider；mode 存 localStorage（key=zhihui:theme），默认 light。
 *  - 切换即写回 localStorage 并重建 theme（useMemo 缓存），全站 MUI 重渲染，无白屏闪烁。
 *  - 同步把设计 token 写入 :root CSS 变量，使 index.css 的 body / .glass 等随主题切换。
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { createAppTheme } from '../theme';
import { THEME_STORAGE_KEY, type ThemeMode } from '../constants/theme';

interface ThemeModeContextValue {
  mode: ThemeMode;
  toggle: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

/** 每种模式下映射到 index.css 的 CSS 变量（与 theme.ts palette 对齐） */
const CSS_VARS: Record<ThemeMode, Record<string, string>> = {
  dark: {
    '--zh-bg-base': '#0a0e1a',
    '--zh-bg-surface': '#121829',
    '--zh-bg-elevated': '#1a2236',
    '--zh-bg-glass': 'rgba(255,255,255,0.04)',
    '--zh-primary': '#22d3ee',
    '--zh-secondary': '#a855f7',
    '--zh-brand': '#34d399',
    '--zh-text-primary': '#e6edf3',
    '--zh-text-secondary': '#9aa7bd',
    '--zh-text-disabled': '#5b6678',
    '--zh-border-subtle': 'rgba(255,255,255,0.08)',
    '--zh-border-strong': 'rgba(255,255,255,0.16)',
    '--zh-status-success': '#34d399',
    '--zh-status-warning': '#fbbf24',
    '--zh-status-error': '#f87171',
    '--zh-status-info': '#22d3ee',
  },
  light: {
    '--zh-bg-base': '#f4f6fb',
    '--zh-bg-surface': '#ffffff',
    '--zh-bg-elevated': '#eef2f9',
    '--zh-bg-glass': 'rgba(255,255,255,0.65)',
    '--zh-primary': '#0891b2',
    '--zh-secondary': '#7c3aed',
    '--zh-brand': '#059669',
    '--zh-text-primary': '#0f172a',
    '--zh-text-secondary': '#5b6678',
    '--zh-text-disabled': '#9aa7bd',
    '--zh-border-subtle': 'rgba(15,23,42,0.08)',
    '--zh-border-strong': 'rgba(15,23,42,0.16)',
    '--zh-status-success': '#059669',
    '--zh-status-warning': '#d97706',
    '--zh-status-error': '#dc2626',
    '--zh-status-info': '#0891b2',
  },
};

/** 读取初始模式：localStorage 优先，无值默认 light（遵循 PRD T1） */
function readInitialMode(): ThemeMode {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    /* 忽略 */
  }
  return 'light';
}

/** 把当前模式的 token 写入 :root CSS 变量，驱动 index.css 全局样式 */
function applyCssVars(mode: ThemeMode): void {
  try {
    const root = document.documentElement;
    const vars = CSS_VARS[mode];
    for (const [k, v] of Object.entries(vars)) {
      root.style.setProperty(k, v);
    }
  } catch {
    /* 忽略 */
  }
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readInitialMode);

  // 持久化 + 同步 CSS 变量（首屏即在 Provider 内按已读 mode 构造 theme，无白屏闪烁）
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      /* 忽略 */
    }
    applyCssVars(mode);
  }, [mode]);

  const value = useMemo<ThemeModeContextValue>(
    () => ({
      mode,
      setMode: setModeState,
      toggle: () => setModeState((m) => (m === 'light' ? 'dark' : 'light')),
    }),
    [mode],
  );

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

/** 获取主题模式控制句柄（仅在 Provider 内使用） */
export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) {
    throw new Error('useThemeMode 必须在 ThemeModeProvider 内使用');
  }
  return ctx;
}
